import React, { ReactElement } from 'react';
import clsx from 'clsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgPlus from '@icons/plus-thick.svg?react';

interface ComponentProps {
  title: string;
  onClick: () => void;
  /** Leading glyph, rendered at 32px before the label. */
  icon?: ReactElement;
  /** Glyph inside the trailing button. Defaults to the plus; override for rows that are not an "add". */
  actionIcon?: ReactElement;
  /** Stretches across the container. Off for tiles that should hug their label. */
  fullWidth?: boolean;
  className?: string;
  dataTestId?: string;
}

/**
 * A full-width action row: an optional leading glyph, the label, and a small button pushed to the
 * right. The whole row is the click target. It defaults to a plus — the "add one of these" row —
 * but `actionIcon` covers rows that are not an add, such as a show/hide toggle.
 *
 * The row is a container rather than a button, because a button cannot be nested inside another
 * one. The trailing button carries a no-op handler and lets the click bubble to the row — giving it
 * the same handler would fire twice — and `group-hover` keeps it reacting along with the label.
 */
const RowButton: React.FC<ComponentProps> = ({ title, onClick, icon, actionIcon, fullWidth = true, className, dataTestId }) => (
  <div
    className={clsx(
      'group flex cursor-pointer flex-row items-center gap-4 rounded-2xl bg-white px-4 py-6 transition-all duration-200',
      // Borderless at rest per the design; the transparent border reserves the space so the row
      // does not shift when it appears on hover, matching how RingilButton reacts.
      'border-2 border-solid border-transparent hover:border-complementary-brandDark',
      fullWidth ? 'w-full' : 'self-start',
      className,
    )}
    onClick={onClick}
    data-testid={dataTestId}
  >
    {icon && <span className={'inline-flex size-8 shrink-0 items-center justify-center'}>{icon}</span>}
    <div className={'flex flex-1 flex-row items-center justify-between gap-4'}>
      <span className={'text-base font-medium tracking-[-0.32px] text-content-brand transition-all duration-200 group-hover:text-complementary-brandDark'}>
        {title}
      </span>
      <RingilButton
        mode={'table'}
        size={'small'}
        className={'group-hover:border-complementary-brandDark group-hover:text-complementary-brandDark'}
        icon={actionIcon ?? <SvgPlus className={'size-4 shrink-0'} />}
        onClick={() => {}}
      />
    </div>
  </div>
);

export default RowButton;
